import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, User, Building2, Target, Calendar, FileText, MapPin, Phone, Mail } from 'lucide-react';

const EntityDataCards = ({ entityType, data = [], count = 0, filterColumns = [], filterInfo = [], displayFields = [] }) => {
  const navigate = useNavigate();
  
  // Show "no data found" message if count is 0 or data is empty
  if (count === 0 || !data || data.length === 0) {
    const displayType = entityType ? entityType.charAt(0).toUpperCase() + entityType.slice(1) : 'Entity';
    return (
      <div className="entity-data-cards my-4">
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 text-center">
          <div className="text-gray-500 dark:text-gray-400 text-sm">
            <p className="font-medium mb-1">No {displayType} found</p>
            <p className="text-xs">No data matches your search criteria.</p>
          </div>
        </div>
      </div>
    );
  }

  // Icon mapping for entity types
  const entityIcons = {
    customer: User,
    lead: Target,
    opportunity: Target,
    activity: Calendar,
    note: FileText,
    contact: User,
    company: Building2
  };

  // Icon mapping helper
  const getIconComponent = (iconName) => {
    const iconMap = {
      'Mail': Mail,
      'Phone': Phone,
      'User': User,
      'Building2': Building2,
      'MapPin': MapPin,
      'Calendar': Calendar,
      'FileText': FileText,
      'Target': Target
    };
    return iconMap[iconName] || null;
  };

  // Format field value based on data type
  const formatFieldValue = (value, dataType, format) => {
    if (value === null || value === undefined) return null;
    
    if (dataType === 'number' || dataType === 'integer' || dataType === 'decimal') {
      if (format === 'currency' || format === 'money') {
        return typeof value === 'number' ? `$${value.toLocaleString()}` : value;
      }
      return typeof value === 'number' ? value.toLocaleString() : value;
    }
    
    if (dataType === 'date' || dataType === 'datetime') {
      try {
        return new Date(value).toLocaleDateString();
      } catch {
        return value;
      }
    }
    
    return value;
  };

  // Field mapping for different entity types - now uses dynamic displayFields from backend
  const getEntityFields = (item, type, filterColumns = [], displayFields = []) => {
    const fields = [];
    const typeLower = (type || '').toLowerCase();
    
    // Helper to check if a field should be shown (always show if it's in filterColumns)
    const shouldShowField = (fieldName) => {
      const fieldLower = fieldName.toLowerCase();
      return filterColumns.some(fc => fc.toLowerCase() === fieldLower) || item[fieldName];
    };
    
    // If displayFields are provided from backend, use them (dynamic approach)
    if (displayFields && displayFields.length > 0) {
      // Add display fields from config (3-4 core fields)
      displayFields.forEach(fieldConfig => {
        const fieldName = fieldConfig.name;
        const value = item[fieldName];
        
        // Always show if it's in filterColumns or if value exists
        if (shouldShowField(fieldName) || value !== null && value !== undefined) {
          const formattedValue = formatFieldValue(value, fieldConfig.dataType, fieldConfig.format);
          if (formattedValue !== null) {
            const IconComponent = fieldConfig.icon ? getIconComponent(fieldConfig.icon) : null;
            fields.push({
              label: fieldConfig.displayName || fieldName,
              value: formattedValue,
              icon: IconComponent
            });
          }
        }
      });
      
      // Add filter columns that are not already in displayFields
      filterColumns.forEach(filterCol => {
        const alreadyAdded = fields.some(f => f.label.toLowerCase() === filterCol.toLowerCase());
        if (!alreadyAdded && item[filterCol] !== null && item[filterCol] !== undefined) {
          const IconComponent = getIconComponent(filterCol); // Try to get icon based on column name
          fields.push({
            label: filterCol,
            value: item[filterCol],
            icon: IconComponent
          });
        }
      });
      
      return fields;
    }
    
    // Fallback to hardcoded mapping if displayFields not provided (backward compatibility)
    switch (typeLower) {
      case 'customer':
        // Always show FullName (FirstName + LastName) first, with Nickname if available
        if (item.FullName) {
          const displayName = item.NickName ? `${item.FullName} (${item.NickName})` : item.FullName;
          fields.push({ label: 'Name', value: displayName, icon: User });
        } else if (item.FirstName || item.LastName) {
          const fullName = `${item.FirstName || ''} ${item.LastName || ''}`.trim();
          const displayName = item.NickName ? `${fullName} (${item.NickName})` : fullName;
          if (displayName) fields.push({ label: 'Name', value: displayName, icon: User });
        } else if (item.CustomerName || item.Name) {
          fields.push({ label: 'Name', value: item.CustomerName || item.Name, icon: User });
        }
        // Show Company if available
        if (item.CompanyName || item.Company) fields.push({ label: 'Company', value: item.CompanyName || item.Company, icon: Building2 });
        // Always show Email if it's in filterColumns or if it exists
        if (shouldShowField('Email') || item.Email) fields.push({ label: 'Email', value: item.Email, icon: Mail });
        // Always show Phone if it's in filterColumns or if it exists
        if (shouldShowField('Phone') || item.Phone) fields.push({ label: 'Phone', value: item.Phone, icon: Phone });
        // Always show NickName if it's in filterColumns
        if (shouldShowField('NickName') && item.NickName) fields.push({ label: 'Nickname', value: item.NickName, icon: User });
        if (item.Address || item.City || item.Country) {
          const address = [item.Address, item.City, item.Country].filter(Boolean).join(', ');
          if (address) fields.push({ label: 'Location', value: address, icon: MapPin });
        }
        if (item.CustomerID) fields.push({ label: 'ID', value: item.CustomerID, type: 'id' });
        break;
        
      case 'lead':
        // Show Title as primary field
        if (item.Title) fields.push({ label: 'Title', value: item.Title, icon: Target });
        // Show ContactName / CompanyName
        if (item.ContactName) {
          fields.push({ label: 'Contact', value: item.ContactName, icon: User });
        } else if (item.CompanyName || item.Company) {
          fields.push({ label: 'Company', value: item.CompanyName || item.Company, icon: Building2 });
        }
        // Always show Email if it's in filterColumns or if it exists
        if (shouldShowField('Email') || item.Email) fields.push({ label: 'Email', value: item.Email, icon: Mail });
        // Always show Phone if it's in filterColumns or if it exists
        if (shouldShowField('Phone') || item.Phone) fields.push({ label: 'Phone', value: item.Phone, icon: Phone });
        // Always show EstimatedBudget/EstimatedValue if it's in filterColumns or if it exists
        if (shouldShowField('EstimatedBudget') || shouldShowField('EstimatedValue') || item.EstimatedBudget || item.EstimatedValue) {
          const value = item.EstimatedBudget || item.EstimatedValue;
          const displayValue = typeof value === 'number' ? `$${value.toLocaleString()}` : value;
          fields.push({ label: 'Estimated Value', value: displayValue });
        }
        if (item.Status) fields.push({ label: 'Status', value: item.Status });
        if (item.LeadID) fields.push({ label: 'ID', value: item.LeadID, type: 'id' });
        break;
        
      case 'opportunity':
        if (item.OpportunityName || item.Name) fields.push({ label: 'Name', value: item.OpportunityName || item.Name });
        if (item.CustomerName || item.Customer) fields.push({ label: 'Customer', value: item.CustomerName || item.Customer, icon: User });
        if (item.Value) fields.push({ label: 'Value', value: typeof item.Value === 'number' ? `$${item.Value.toLocaleString()}` : item.Value });
        if (item.Stage) fields.push({ label: 'Stage', value: item.Stage });
        if (item.CloseDate) fields.push({ label: 'Close Date', value: new Date(item.CloseDate).toLocaleDateString(), icon: Calendar });
        if (item.OpportunityID) fields.push({ label: 'ID', value: item.OpportunityID, type: 'id' });
        break;
        
      case 'activity':
        if (item.Subject || item.Name) fields.push({ label: 'Subject', value: item.Subject || item.Name });
        if (item.ActivityType) fields.push({ label: 'Type', value: item.ActivityType });
        if (item.Status) fields.push({ label: 'Status', value: item.Status });
        if (item.DueDate || item.StartDate) {
          const date = item.DueDate || item.StartDate;
          fields.push({ label: 'Date', value: new Date(date).toLocaleDateString(), icon: Calendar });
        }
        if (item.ActivityID) fields.push({ label: 'ID', value: item.ActivityID, type: 'id' });
        break;
        
      default:
        // Generic fallback - show first few fields
        Object.entries(item).slice(0, 5).forEach(([key, value]) => {
          if (value !== null && value !== undefined && key !== 'id' && !key.toLowerCase().includes('id')) {
            fields.push({ label: key, value: String(value).substring(0, 50) });
          }
        });
        // Add ID if available
        const idField = Object.keys(item).find(k => k.toLowerCase().endsWith('id'));
        if (idField && item[idField]) {
          fields.push({ label: 'ID', value: item[idField], type: 'id' });
        }
    }
    
    return fields;
  };

  // Get entity ID and route
  const getEntityRoute = (item, type) => {
    const typeLower = (type || '').toLowerCase();
    const idField = typeLower === 'customer' ? 'CustomerID' :
                    typeLower === 'lead' ? 'LeadID' :
                    typeLower === 'opportunity' ? 'OpportunityID' :
                    typeLower === 'activity' ? 'ActivityID' :
                    typeLower === 'note' ? 'NoteID' :
                    Object.keys(item).find(k => k.toLowerCase().endsWith('id'));
    
    const id = item[idField];
    if (!id) return null;
    
    const route = typeLower === 'customer' ? 'customers' :
                  typeLower === 'lead' ? 'leads' :
                  typeLower === 'opportunity' ? 'opportunities' :
                  typeLower === 'activity' ? 'activities' :
                  typeLower === 'note' ? 'notes' :
                  typeLower;
    
    return { id, route: `/${route}/${id}` };
  };

  const Icon = entityIcons[entityType?.toLowerCase()] || FileText;
  const displayType = entityType ? entityType.charAt(0).toUpperCase() + entityType.slice(1) : 'Entity';

  return (
      <div className="entity-data-cards my-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <Icon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              {displayType} Results {count > 0 && `(${count})`}
            </h3>
          </div>
        </div>
        
      
      <div className="grid grid-cols-1 gap-3" style={{ width: '90%' }}>
        {data.slice(0, 12).map((item, index) => {
          const fields = getEntityFields(item, entityType, filterColumns, displayFields);
          const entityRoute = getEntityRoute(item, entityType);
          
          return (
            <div
              key={index}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 hover:shadow-md transition-shadow w-full"
            >
              <div className="space-y-2">
                {fields.slice(0, 4).map((field, fieldIndex) => {
                  const FieldIcon = field.icon;
                  return (
                    <div key={fieldIndex} className="flex items-start space-x-2 text-xs">
                      {FieldIcon && <FieldIcon className="h-3.5 w-3.5 text-gray-400 mt-0.5 flex-shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <span className="text-gray-500 dark:text-gray-400 font-medium">
                          {field.label}:
                        </span>
                        <span className={`ml-1 ${field.type === 'id' ? 'text-blue-600 dark:text-blue-400 font-mono' : 'text-gray-700 dark:text-gray-300'}`}>
                          {field.value}
                        </span>
                      </div>
                    </div>
                  );
                })}
                
                {entityRoute && (
                  <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(entityRoute.route);
                      }}
                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center space-x-1 cursor-pointer bg-transparent border-none p-0"
                    >
                      <span>View Details</span>
                      <ExternalLink className="h-3 w-3" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      
      {data.length > 12 && (
        <div className="mt-3 text-xs text-gray-500 dark:text-gray-400 text-center">
          Showing 12 of {data.length} results
        </div>
      )}
    </div>
  );
};

export default EntityDataCards;

